import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { buildCollectionEventFilter } from 'utils/collection-filters.utils';
import { getCurrentISOString } from 'utils/date.utils';

import { EVENTS_PER_PAGE_COLLECTION } from 'constants/events.constant';
import { useLocale } from 'hooks/useLocale';

import { sortByStartDateTime } from 'components/@events/utils';

import { collectionEventsQuery } from './queries';

interface Props {
  collectionId?: string;
  eventsPerPage: number;
  collectionFilters?: {
    timeFilter?: string | null;
    venue?: { id: string } | null;
  };
}

export const useInfiniteEventsCollection = ({
  collectionId,
  eventsPerPage,
  collectionFilters,
}: Props) => {
  const { locale } = useLocale();

  const eventFilter = useMemo(() => {
    return buildCollectionEventFilter(
      {
        collections: { allIn: [collectionId] },
        endDatetime: { gte: getCurrentISOString() },
        unlistedEvent: { eq: false },
      },
      collectionFilters,
    );
  }, [collectionId, collectionFilters]);

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery(
    collectionEventsQuery(
      {
        locale,
        filter: eventFilter,
        first: EVENTS_PER_PAGE_COLLECTION,
        orderBy: 'startDatetime_ASC',
        skip: 0,
      },
      eventsPerPage,
    ),
  );

  const unfilteredEvents = data?.pages.flatMap((page) => page.events);
  const events = unfilteredEvents?.sort((a, b) =>
    sortByStartDateTime(a, b, 'asc'),
  );

  return {
    events,
    fetchNextPage,
    hasNextPage,
    isFetching,
  };
};
